import React from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { paymentTypeIdToLabel } from '../../utils/payment-type-helper'
import _startCase from 'lodash/startCase'
import { retrieveConfig } from '../../config/config'
import PaymentModelLoader from '../payment-model-loader'
import GeneralError from '../general-error'
import { validate as isValidUUID } from 'uuid'
import ReceiptSuccess from './success'
import ReceiptFailure from './failure'
import { mapErrorMessage } from '../../utils/error-message-mapper'

export default function Receipt() {
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const chargeId = queryParams.get('charge_id')
    const status = queryParams.get('status') // Get status from URL params

    const [receiptDetails, setReceiptDetails] = React.useState(null)
    const [config, setConfig] = React.useState(null)
    const [paymentStatus, setPaymentStatus] = React.useState(status || 'loading')

    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState(null)

    React.useEffect(() => {
        ;(async function loadConfig() {
            const result = await retrieveConfig()
            setConfig(result)
        })()
    }, [])

    React.useEffect(() => {
        getReceiptData(chargeId, setLoading, setError)
            .then(data => {
                // Check payment status from the data
                const paymentCompleted = data.status?.status === 'completed'
                const hasError = data.error_message || data.psp_responses?.[0]?.psp_error
                const isRetryable = data.error_message || data.psp_responses?.[0]?.psp_error?.is_retryable

                if (paymentCompleted && !hasError) {
                    setPaymentStatus('success')
                    setReceiptDetails(buildReceiptDetails(data))
                } else {
                    setPaymentStatus('failed')
                    const rawErrorMsg =
                        data.error_message ||
                        data.psp_responses?.[0]?.psp_error?.error_message ||
                        'Payment was not completed successfully.'

                    // Map the raw error message to a user-friendly message
                    const mappedError = mapErrorMessage(rawErrorMsg)

                    setError({
                        message: mappedError.message,
                        isRetryable: isRetryable,
                        receiptData: data,
                    })
                }
            })
            .catch(e => {
                setPaymentStatus('failed')
                setError('Failed to retrieve receipt data.')
                console.error('Failed to retrieve receipt data', e)
            })
    }, [chargeId])

    const isSuccess = paymentStatus === 'success' && receiptDetails && !error
    const isFailure = paymentStatus === 'failed' || error

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                height: '100vh',
                backgroundColor: config?.backgroundColor,
            }}
        >
            <div style={{ width: 530, margin: '80px 0px' }}>
                <GeneralError visible={error && !isFailure} message={error} />
                {loading && <PaymentModelLoader />}
                {!loading && isSuccess && <ReceiptSuccess visible={true} chargeId={chargeId} receiptDetails={receiptDetails} />}
                {!loading && isFailure && <ReceiptFailure visible={true} chargeId={chargeId} message={error} />}
            </div>
        </div>
    )
}

async function getReceiptData(chargeId, setLoading) {
    if (!isValidUUID(chargeId)) throw new Error('Invalid charge ID')
    setLoading(true)
    return (
        axios
            // Base URL is deployment-controlled env config; path is hardcoded; chargeId is UUID-validated above
            // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
            .get(window.env.REACT_APP_DEMO_BACKEND_URL + '/payment/receipt/' + chargeId, {
                headers: {
                    domain: window.location.hostname,
                },
            })
            .then(receiptResponse => receiptResponse.data)
            .finally(() => {
                setLoading(false)
            })
    )
}
const getWalletName = walletId => {
    // if Doku wallet, get the name from the middle of the ID
    if (walletId.includes('_')) {
        const parts = walletId.split('_')
        return _startCase(parts[1])
    }

    return _startCase(walletId)
}

export const buildReceiptDetails = receiptData => {
    const cardEndingNumber = receiptData.payments[0].payment_instrument.instrument.card_last_four
    const paymentType = receiptData.payments[0].payment_instrument.instrument_type
    const pspResponse = receiptData.psp_responses[0]

    let payDetails
    if (paymentType === 'card_instrument') {
        payDetails = {
            cardholderName: receiptData.payments[0].payment_instrument.payer.entity_details.name.full_name,
            cardholderNumber: '....' + cardEndingNumber,
            expiryDate: receiptData.payments[0].payment_instrument.instrument.expiration_date,
        }
    } else if (paymentType === 'wallet_india_instrument') {
        payDetails = {
            walletName: _startCase(receiptData.payments[0].payment_instrument.instrument.wallet_india_name) ?? 'Unknown',
        }
    } else if (paymentType === 'upi_wallet_instrument') {
        payDetails = {
            walletName: getWalletName(receiptData.payments[0].payment_instrument.instrument.wallet_name) ?? 'Unknown',
        }
    } else if (paymentType === 'wallet_instrument') {
        payDetails = {
            paymentType: 'Wallet',
            walletName: _startCase(receiptData.payments[0].payment_instrument.instrument.wallet_brand) ?? 'Unknown',
        }
    } else {
        payDetails = {
            bankName:
                receiptData.payments[0].payment_instrument.instrument.bank_code ||
                _startCase(receiptData.payments[0].payment_instrument.instrument.acquirer),
        }
    }

    // TODO look into passing response message and code, omit for now
    const commonPaymentFields = {
        receiptNo: receiptData.charge_id,
        // status: 'Approved',
        // response: 'Honour with identification (08)',
        paymentDate: receiptData.session?.start_timestamp || null,
        paymentInstrument: {
            ...receiptData.payments[0]?.payment_instrument?.instrument,
            instrument_type: receiptData.payments[0]?.payment_instrument?.instrument_type,
        },

        paymentAmount: (receiptData.order?.order_amount?.value / 100).toFixed(2),
        paymentType: paymentTypeIdToLabel(paymentType, pspResponse),
    }

    return {
        ...commonPaymentFields,
        ...payDetails,
    }
}
